import React from "react";
import { Box, Grid, alpha } from "@mui/material";
import { MotionBox } from "@rusha/shared-theme";
import CloudIcon from "@mui/icons-material/Cloud";
import StorageIcon from "@mui/icons-material/Storage";
import TerminalIcon from "@mui/icons-material/Terminal";
import type { Theme } from "@mui/material/styles";
import {
  visualContainerInitial,
  visualContainerInView,
  visualContainerTransition,
  outerRingAnimate,
  outerRingTransition,
  innerRingAnimate,
  innerRingTransition,
  ringDotTransition,
  outerRingDotAnimate,
  innerRingDotAnimate,
  centerCardAnimate,
  centerCardTransition,
  cursorAnimate,
  cursorTransition,
  connectionLineAnimate,
  connectionLineTransition,
  connectionLine2Transition,
} from "./HeroAnimations";

interface HeroVisualProps {
  theme: Theme;
}

const HeroVisual: React.FC<HeroVisualProps> = ({ theme }) => (
  <Grid item xs={12} md={6}>
    <MotionBox
      sx={{
        position: "relative",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        minHeight: { xs: 300, sm: 400 },
        mt: { xs: 2, md: 0 },
      }}
      initial={visualContainerInitial}
      whileInView={visualContainerInView}
      viewport={{ once: true }}
      transition={visualContainerTransition}
    >
      {/* Outer orbital ring */}
      <MotionBox
        sx={{
          position: "absolute",
          width: { xs: 240, sm: 280, md: 320 },
          height: { xs: 240, sm: 280, md: 320 },
          borderRadius: "50%",
          border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
          display: { xs: "none", sm: "block" },
        }}
        animate={outerRingAnimate}
        transition={outerRingTransition}
      >
        <MotionBox
          sx={{
            position: "absolute",
            top: "0%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: { xs: 20, sm: 25 },
            height: { xs: 20, sm: 25 },
            borderRadius: "50%",
            backgroundColor: theme.palette.primary.main,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            boxShadow: `0 0 15px ${alpha(theme.palette.primary.main, 0.7)}`,
          }}
          animate={outerRingDotAnimate(theme)}
          transition={ringDotTransition}
        >
          <CloudIcon sx={{ fontSize: 14, color: "white" }} />
        </MotionBox>
      </MotionBox>

      {/* Inner orbital ring */}
      <MotionBox
        sx={{
          position: "absolute",
          width: { xs: 180, sm: 220, md: 260 },
          height: { xs: 180, sm: 220, md: 260 },
          borderRadius: "50%",
          border: `1px solid ${alpha(theme.palette.secondary.main, 0.25)}`,
          display: { xs: "none", sm: "block" },
        }}
        animate={innerRingAnimate}
        transition={innerRingTransition}
      >
        <MotionBox
          sx={{
            position: "absolute",
            top: "100%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: { xs: 20, sm: 25 },
            height: { xs: 20, sm: 25 },
            borderRadius: "50%",
            backgroundColor: theme.palette.secondary.main,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            boxShadow: `0 0 15px ${alpha(theme.palette.secondary.main, 0.7)}`,
          }}
          animate={innerRingDotAnimate(theme)}
          transition={ringDotTransition}
        >
          <StorageIcon sx={{ fontSize: 14, color: "white" }} />
        </MotionBox>
      </MotionBox>

      {/* Center card */}
      <MotionBox
        sx={{
          position: "relative",
          width: { xs: 150, sm: 170, md: 190 },
          height: { xs: 150, sm: 170, md: 190 },
          backgroundColor: theme.palette.primary.main,
          backgroundImage: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
          borderRadius: "12px",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          color: "white",
          fontWeight: "bold",
          fontSize: { xs: "1.7rem", sm: "1.9rem", md: "2.2rem" },
          boxShadow: `0 20px 80px ${alpha(theme.palette.primary.main, 0.5)}, 0 0 20px ${alpha(theme.palette.primary.main, 0.3)}`,
          overflow: "hidden",
          zIndex: 3,
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: "1px",
            background: alpha(theme.palette.background.paper, 0.3),
          },
        }}
        animate={centerCardAnimate}
        transition={centerCardTransition}
      >
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            background: `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.2)} 0%, ${alpha(theme.palette.background.paper, 0)} 50%)`,
            borderRadius: "12px",
          }}
        />
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            position: "relative",
            zIndex: 5,
            textShadow: `0 0 10px ${alpha(theme.palette.common.white, 0.5)}`,
          }}
        >
          <TerminalIcon
            sx={{ fontSize: { xs: 18, sm: 22, md: 24 }, opacity: 0.9 }}
          />
          Rusha
        </Box>

        <MotionBox
          sx={{
            width: "12px",
            height: "3px",
            backgroundColor: "white",
            mt: 1,
            opacity: 0.9,
            boxShadow: `0 0 10px ${alpha(theme.palette.common.white, 0.8)}`,
          }}
          animate={cursorAnimate}
          transition={cursorTransition}
        />
      </MotionBox>

      {/* Connection lines */}
      <MotionBox
        sx={{
          position: "absolute",
          width: "45%",
          height: "2px",
          background: `linear-gradient(90deg, ${alpha(theme.palette.primary.main, 0)} 0%, ${alpha(theme.palette.primary.main, 0.5)} 50%, ${alpha(theme.palette.primary.main, 0)} 100%)`,
          top: "45%",
          left: "5%",
          zIndex: 2,
          "&::before": {
            content: '""',
            position: "absolute",
            width: "6px",
            height: "6px",
            borderRadius: "50%",
            backgroundColor: theme.palette.primary.main,
            right: "0",
            top: "-2px",
            boxShadow: `0 0 8px ${theme.palette.primary.main}`,
          },
          display: { xs: "none", sm: "block" },
        }}
        animate={connectionLineAnimate}
        transition={connectionLineTransition}
      />

      <MotionBox
        sx={{
          position: "absolute",
          width: "45%",
          height: "2px",
          background: `linear-gradient(90deg, ${alpha(theme.palette.primary.main, 0)} 0%, ${alpha(theme.palette.primary.main, 0.5)} 50%, ${alpha(theme.palette.primary.main, 0)} 100%)`,
          top: "55%",
          right: "5%",
          zIndex: 2,
          "&::before": {
            content: '""',
            position: "absolute",
            width: "6px",
            height: "6px",
            borderRadius: "50%",
            backgroundColor: theme.palette.primary.main,
            left: "0",
            top: "-2px",
            boxShadow: `0 0 8px ${theme.palette.primary.main}`,
          },
          display: { xs: "none", sm: "block" },
        }}
        animate={connectionLineAnimate}
        transition={connectionLine2Transition}
      />
    </MotionBox>
  </Grid>
);

export default HeroVisual;
